import {Collapse, Divider, Loader, Modal} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {type FC, useEffect, useState} from 'react';
import {MdAdd, MdClose, MdKeyboardArrowDown} from 'react-icons/md';
import {cn} from '../../../../lib/classname';
import {
  type ColumnSectionNameList,
  getColumnSectionNameList,
} from '../../usecases/getColumnSectionNameList';

const TableOfContentsModal = ({
  sectionNameList,
  isOpen,
  onClose,
}: {
  sectionNameList?: ColumnSectionNameList;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      withCloseButton={false}
      styles={{
        root: {
          borderRadius: 20,
        },
      }}
    >
      <h2 className="text-md pc:text-lg font-bold border-b-[1px] border-line flex justify-center items-center">
        <div
          className="p-4 hover:pointer hover:opacity-50 absolute left-8"
          onClick={onClose}
        >
          <MdClose size={20} />
        </div>
        <span>目次</span>
      </h2>
      <ul onClick={onClose} className="space-y-12 mt-12">
        {sectionNameList?.map((item, i) => (
          <li key={item.sectionName} className="pt-4 first:pt-0">
            <a href={`#section-${i + 1}`} className="font-bold">
              <span>{i + 1}.&nbsp;</span>
              {item.sectionName}
            </a>
            {item.childSectionNameList.length > 0 && (
              <ul className="space-y-12 pl-16">
                {item.childSectionNameList?.map((child, j) => (
                  <li key={child}>
                    <a
                      href={`#section-${i + 1}-${j + 1}`}
                      className="font-bold"
                    >
                      <span>
                        {i + 1}.{j + 1}.&nbsp;
                      </span>
                      {child}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Modal>
  );
};

type Props = {
  className?: string;
  closeTableOfContentsModal: () => void;
  isOpenTableOfContentsModal: boolean;
};
export const TableOfContents: FC<Props> = ({
  className,
  closeTableOfContentsModal,
  isOpenTableOfContentsModal,
}) => {
  const [opened, {toggle}] = useDisclosure(false);

  const [sectionNameList, setSectionNameList] = useState<ColumnSectionNameList>(
    [],
  );

  useEffect(() => {
    // GetColumnSectionNameList関数内で遷移先用のidを設定している
    setSectionNameList(getColumnSectionNameList());
  }, []);

  return (
    <div className={cn(className)}>
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'w-full bg-white shadow rounded-16 pc:rounded-b-4 text-left px-24 py-16 flex items-center gap-[4px]',
          opened && 'rounded-b-none pc:rounded-b-none',
        )}
      >
        <span className="font-bold">目次</span>
        {opened ? <MdKeyboardArrowDown size="24px" /> : <MdAdd size="24px" />}
      </button>
      <Collapse in={opened} transitionDuration={500}>
        <div className="bg-white shadow px-24 pb-16 rounded-b-16 pc:rounded-b-4">
          <Divider className="mb-12" />
          <ul className="space-y-12">
            {sectionNameList.length > 0 ? (
              sectionNameList.map((item, i) => (
                <li key={item.sectionName} className="pt-4 first:pt-0">
                  <a href={`#section-${i + 1}`} className="font-bold">
                    <span>{i + 1}.&nbsp;</span>
                    {item.sectionName}
                  </a>
                  {item.childSectionNameList.length > 0 && (
                    <ul className="space-y-12 pl-16">
                      {item.childSectionNameList?.map((child, j) => (
                        <li key={child}>
                          <a
                            href={`#section-${i + 1}-${j + 1}`}
                            className="font-bold"
                          >
                            <span>
                              {i + 1}.{j + 1}.&nbsp;
                            </span>
                            {child}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))
            ) : (
              <>
                <div className="w-full h-[150px] flex justify-center items-center">
                  <Loader color="gray" />
                </div>
              </>
            )}
          </ul>
        </div>
      </Collapse>
      <TableOfContentsModal
        isOpen={isOpenTableOfContentsModal}
        onClose={closeTableOfContentsModal}
        sectionNameList={sectionNameList}
      />
    </div>
  );
};
